import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  PrimaryButton,
  SecondaryButton,
  NakedButton,
  Text,
  LockIcon,
  useTooltip,
} from '@weave/design-system';

interface ChecklistFooterProps {
  onCancel: () => void;
  onSaveAndClose: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
  isSubmitDisabled: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  allowExceptions?: boolean;
  error?: string | null;
}

export const ChecklistFooter = ({
  allowExceptions = false,
  onCancel,
  onSaveAndClose,
  onSubmit,
  isLoading,
  isSubmitDisabled,
  isSaving,
  isSubmitting,
  error,
}: ChecklistFooterProps) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ placement: 'top-end' });
  const isSubmitActionDisabled =
    isLoading || isSubmitDisabled || isSaving || isSubmitting || Boolean(error);
  const submitDisabledReason = error
    ? error
    : isLoading
    ? 'Evaluating required checklist items...'
    : isSubmitting
    ? 'Submitting handoff...'
    : allowExceptions
    ? 'One or more required checks have not passed. Resolve them or leave an exception note to continue.'
    : 'One or more required checks have not passed. Resolve them to continue.';

  return (
    <div css={styles.footerContainer}>
      <div css={styles.actionItem}>
        <NakedButton onClick={onCancel} disabled={isSaving || isSubmitting}>
          <Text as="span" color="primary">
            Cancel
          </Text>
        </NakedButton>
      </div>

      <div css={styles.actionItem}>
        <SecondaryButton onClick={onSaveAndClose} disabled={isSaving || isSubmitting}>
          {isSaving ? 'Saving...' : 'Save & Close'}
        </SecondaryButton>
      </div>

      <div
        css={styles.submitWrapper}
        title={isSubmitActionDisabled ? submitDisabledReason : undefined}
        {...(isSubmitActionDisabled ? triggerProps : {})}
      >
        <div css={styles.actionItem}>
          <PrimaryButton onClick={onSubmit} disabled={isSubmitActionDisabled}>
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <span css={styles.submitLabel}>
                {isSubmitActionDisabled && <LockIcon color="light" size={14} />}
                Submit Handoff
              </span>
            )}
          </PrimaryButton>
        </div>

        {isSubmitActionDisabled && (
          <Tooltip {...tooltipProps} css={styles.tooltip}>
            {submitDisabledReason}
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const styles = {
  footerContainer: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.spacing(2.5)};
    width: 100%;
  `,
  actionItem: css`
    display: inline-flex;
    width: fit-content;
    flex: 0 0 auto;
  `,
  submitWrapper: css`
    display: inline-flex;
    width: fit-content;
    flex: 0 0 auto;
  `,
  submitLabel: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing(0.75)};
  `,
  tooltip: css`
    && {
      max-width: 240px;
    }
  `,
};
